import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Users, Building2, CalendarDays, HandHeart, TrendingUp, MapPin, Download, PoundSterling, RefreshCw, FileText,
} from "lucide-react";
import { api } from "@/lib/api";

const WINDOW_OPTIONS = [
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
    { value: 90, label: "90 days" },
    { value: 180, label: "6 months" },
    { value: 365, label: "12 months" },
];

const Tile = ({ icon: Icon, value, label, tint = "primary", testid }) => (
    <div
        data-testid={testid}
        className={`rounded-3xl border border-border bg-surface p-5 flex flex-col justify-between min-h-[132px]`}
    >
        <div className={`h-9 w-9 rounded-2xl grid place-items-center ${tint === "primary" ? "bg-primary/10 text-primary" : tint === "accent" ? "bg-accent/20 text-accent-foreground" : "bg-secondary/40 text-secondary-foreground"}`}>
            <Icon className="h-4 w-4" />
        </div>
        <div>
            <div className="font-display font-black text-3xl leading-none">{value}</div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">{label}</div>
        </div>
    </div>
);

const fmt = (n) => new Intl.NumberFormat("en-GB").format(n || 0);
const money = (n, cur = "GBP") =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(n || 0);

export default function Impact() {
    const [days, setDays] = useState(90);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [grantAmount, setGrantAmount] = useState("");
    const [grantLabel, setGrantLabel] = useState("");
    const [busy, setBusy] = useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.impactSummary(days);
            setSnapshot(res);
            setGrantAmount(res?.grant?.grant_amount ? String(res.grant.grant_amount) : "");
            setGrantLabel(res?.grant?.grant_period_label || "");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not load impact data");
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        load();
    }, [load]);

    const saveGrant = async () => {
        setBusy(true);
        try {
            await api.setGrantConfig({
                grant_amount: Number(grantAmount) || 0,
                grant_period_label: grantLabel || "annual",
            });
            toast.success("Grant configuration saved");
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not save grant");
        } finally {
            setBusy(false);
        }
    };

    const maxGeo = useMemo(
        () => Math.max(1, ...(snapshot?.geography || []).map((g) => g.count)),
        [snapshot],
    );

    if (loading && !snapshot) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-sm text-muted-foreground">
                Loading impact data…
            </div>
        );
    }

    const head = snapshot?.headline || {};
    const reach = snapshot?.reach || {};
    const grant = snapshot?.grant || {};
    const cur = grant.grant_currency || "GBP";

    return (
        <div data-testid="impact-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Impact</span>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Funder dashboard</h1>
                    <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
                        Reach, engagement and social value generated by Blackrod Now. Download the PDF report for board meetings and grant reviews.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        data-testid="impact-window"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="px-4 py-2 rounded-full border border-border bg-surface text-sm"
                    >
                        {WINDOW_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={load}
                        className="p-2 rounded-full border border-border hover:bg-muted"
                        aria-label="Refresh"
                        data-testid="impact-refresh"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </button>
                    <a
                        data-testid="impact-download-short"
                        href={api.impactPdfUrl(days, "short")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-muted"
                    >
                        <FileText className="h-4 w-4" /> Short PDF
                    </a>
                    <a
                        data-testid="impact-download-full"
                        href={api.impactPdfUrl(days, "full")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
                    >
                        <Download className="h-4 w-4" /> Full PDF
                    </a>
                </div>
            </div>

            {/* Headline tiles */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <Tile testid="tile-unique-residents" icon={Users} value={fmt(head.unique_residents)} label="Unique residents engaged" />
                <Tile testid="tile-orgs-live" icon={Building2} value={fmt(head.orgs_live)} label="Organisations live" tint="accent" />
                <Tile testid="tile-events-live" icon={CalendarDays} value={fmt(head.events_live)} label="Events published" tint="secondary" />
                <Tile testid="tile-volunteer-conv" icon={HandHeart} value={fmt(head.volunteer_conversions)} label="Volunteer signups" tint="accent" />
                <Tile testid="tile-event-views" icon={TrendingUp} value={fmt(reach.event_views)} label="Event page views" tint="secondary" />
                <Tile testid="tile-org-views" icon={TrendingUp} value={fmt(reach.org_views)} label="Organisation views" tint="secondary" />
                <Tile testid="tile-shares" icon={TrendingUp} value={fmt(reach.share_clicks)} label="Share clicks" tint="secondary" />
                <Tile
                    testid="tile-cost-per-resident"
                    icon={PoundSterling}
                    value={grant.grant_amount ? money(head.cost_per_resident || 0, cur) : "—"}
                    label="Cost per resident"
                />
            </div>

            {/* Social value + Grant config */}
            <div className="mt-6 grid lg:grid-cols-3 gap-4">
                <div className="rounded-3xl border border-border bg-surface p-6 lg:col-span-2">
                    <h2 className="font-display font-black text-xl">Social value generated</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Estimated volunteer time and value delivered through Blackrod Now in the selected window.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-3 gap-3">
                        <div className="rounded-2xl bg-background border border-border p-4">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Volunteer signups</div>
                            <div className="mt-1 font-display font-black text-3xl text-primary">{fmt(head.volunteer_conversions)}</div>
                        </div>
                        <div className="rounded-2xl bg-background border border-border p-4">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Hours (est.)</div>
                            <div className="mt-1 font-display font-black text-3xl">{fmt(head.volunteer_hours_estimated)}</div>
                        </div>
                        <div className="rounded-2xl bg-background border border-border p-4">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Value (£15/hr)</div>
                            <div className="mt-1 font-display font-black text-3xl">{money(head.volunteer_value_estimated || 0, cur)}</div>
                        </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-4">
                        Volunteer hours are estimated at 4 hours per contact-button click. Valued using the UK volunteer social value multiplier of £15 per hour.
                    </p>
                </div>

                <div className="rounded-3xl border border-border bg-surface p-6" data-testid="grant-config-card">
                    <h2 className="font-display font-black text-xl">Grant configuration</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Enter the current grant amount to calculate cost per resident engaged.
                    </p>
                    <label className="mt-4 block text-sm">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Grant amount ({cur})</span>
                        <input
                            data-testid="grant-amount-input"
                            type="number"
                            min="0"
                            step="100"
                            value={grantAmount}
                            onChange={(e) => setGrantAmount(e.target.value)}
                            className="mt-1 w-full px-3 py-2 rounded-2xl border border-border bg-background"
                        />
                    </label>
                    <label className="mt-3 block text-sm">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Grant period label</span>
                        <input
                            value={grantLabel}
                            onChange={(e) => setGrantLabel(e.target.value)}
                            placeholder="e.g. Annual 2026"
                            className="mt-1 w-full px-3 py-2 rounded-2xl border border-border bg-background"
                        />
                    </label>
                    <button
                        type="button"
                        data-testid="grant-save"
                        disabled={busy}
                        onClick={saveGrant}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-black uppercase tracking-wider disabled:opacity-60"
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* Geography */}
            <div className="mt-6 grid lg:grid-cols-2 gap-4">
                <div className="rounded-3xl border border-border bg-surface p-6">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <h2 className="font-display font-black text-xl">Geographic reach</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Listings by UK postcode outward code (ward-level, GDPR-safe).</p>
                    <div className="mt-4 space-y-2">
                        {(snapshot?.geography || []).slice(0, 12).map((g) => (
                            <div key={g.postcode} className="flex items-center gap-3" data-testid={`geo-${g.postcode}`}>
                                <div className="w-14 text-sm font-bold">{g.postcode}</div>
                                <div className="flex-1 rounded-full bg-muted h-6 overflow-hidden">
                                    <div
                                        className="h-full bg-primary"
                                        style={{ width: `${Math.round((g.count / maxGeo) * 100)}%` }}
                                    />
                                </div>
                                <div className="w-10 text-right text-sm text-muted-foreground">{g.count}</div>
                            </div>
                        ))}
                        {!snapshot?.geography?.length && (
                            <p className="text-sm text-muted-foreground">
                                No postcode data yet — encourage organisations to add a postcode in their venue/address fields.
                            </p>
                        )}
                    </div>
                </div>

                <div className="rounded-3xl border border-border bg-surface p-6">
                    <h2 className="font-display font-black text-xl">Top organisations</h2>
                    <p className="text-sm text-muted-foreground mt-1">Ranked by follower count in the selected window.</p>
                    <div className="mt-4 space-y-2">
                        {(snapshot?.top_orgs || []).map((o) => (
                            <div key={o.slug} className="flex items-center justify-between rounded-2xl bg-background border border-border px-4 py-3">
                                <div className="text-sm font-semibold truncate">{o.name}</div>
                                <div className="text-xs text-muted-foreground">{o.followers} follower{o.followers === 1 ? "" : "s"}</div>
                            </div>
                        ))}
                        {!snapshot?.top_orgs?.length && (
                            <p className="text-sm text-muted-foreground">No follower data yet.</p>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-8 text-[11px] text-muted-foreground">
                Generated {snapshot?.generated_at?.slice(0, 10)} · window {snapshot?.window_days} days · postcodes are outward code only (no fine-grained tracking).
            </p>
        </div>
    );
}
